import useMaterial from "@/hooks/useMaterial";
import { useAppStore } from "@/stores/appStore";
import Plank from "../Plank";
import { useLayoutEffect, useState, useRef, useMemo, useEffect } from "react";
import * as THREE from "three";
import { Geometry, Base, Addition, Subtraction, ReverseSubtraction, Intersection, Difference } from "@react-three/csg";
import { useFrame } from "@react-three/fiber";
import { min } from "node_modules/cypress/types/lodash";
import { materialOptions } from "@/theme/constants";
import { rotateUVs } from "@/services/helpers/rotateUV";
import { RoundedBox } from "@react-three/drei";
import { config_base } from "@/data/config_base";
import Mailbox from "../Modifiers/Mailbox";
import { useContent } from "@/hooks/useContent";
import useCopyTexture from "@/hooks/useCopyTexture";

const PlankGroupSmall = ({ outerHeight, plankSize, minGap, material, position, pillarPosition }) => {
	const { pillar } = useAppStore((state) => state.configurator);

	const textures = useCopyTexture(pillar.selectedMaterials[pillar.id]?.textures, {
		repeat: pillar.selectedMaterials[pillar.id]?.settings.repeat,
	});

	return (
		<group position={position}>
			<Plank
				material={material}
				position={{ x: 0, y: 0, z: 0 }}
				width={plankSize.smallWidth}
				height={plankSize.height}
				length={plankSize.length}
				radius={0.0008}
				bevelSegments={2}
				smoothness={0.0008}
				textures={textures}
				pillarPosition={pillarPosition}
			/>
			<Plank
				material={material}
				position={{ x: plankSize.smallWidth + minGap, y: 0, z: 0 }}
				width={plankSize.smallWidth}
				height={plankSize.height}
				length={plankSize.length}
				radius={0.0008}
				bevelSegments={2}
				smoothness={0.0008}
				textures={textures}
				pillarPosition={pillarPosition}
			/>
		</group>
	);
};

const PlankGroupLarge = ({ plankSize, minGap, material, position, pillarPosition }) => {
	const { pillar } = useAppStore((state) => state.configurator);

	const textures = useCopyTexture(pillar.selectedMaterials[pillar.id]?.textures, {
		repeat: pillar.selectedMaterials[pillar.id]?.settings.repeat,
	});

	return (
		<group position={position}>
			<Plank
				material={material}
				position={{ x: -(plankSize.smallWidth / 2) - minGap, y: 0, z: plankSize.largeWidth / 2 - plankSize.length / 2 }}
				width={plankSize.largeWidth - minGap / 2}
				height={plankSize.height}
				length={plankSize.length}
				radius={0.0008}
				bevelSegments={2}
				smoothness={0.0008}
				rotation={[0, Math.PI / 2, 0]}
				textures={textures}
				pillarPosition={pillarPosition}
			/>
			<Plank
				material={material}
				position={{
					x: -(plankSize.smallWidth / 2) - minGap,
					y: 0,
					z: plankSize.largeWidth / 2 + (plankSize.largeWidth - plankSize.length / 2),
				}}
				width={plankSize.largeWidth - minGap / 2}
				height={plankSize.height}
				length={plankSize.length}
				radius={0.0008}
				bevelSegments={2}
				smoothness={0.0008}
				rotation={[0, Math.PI / 2, 0]}
				textures={textures}
				pillarPosition={pillarPosition}
			/>
		</group>
	);
};

const ActiveModifier = ({ mod, pillarPosition, position }) => {
	if (pillarPosition === "left") return;

	return <>{mod.id === "mailbox" && <Mailbox position={position} />}</>;
};

export default function PillarStockholm({ doorWidth, outerHeight, pillarDimensions, minGap = 0.001, pillarPosition }) {
	const { pillar } = useAppStore((state) => state.configurator);
	const { getModifiers } = useContent();
	const meshRef = useRef();
	const lastGeometryRef = useRef<THREE.BufferGeometry | null>(null);

	let selectedModifiers = pillar.selectedModifiers.filter((selected) => pillar.modifiers.includes(selected.id));

	selectedModifiers = getModifiers().filter((modifier) =>
		selectedModifiers.some((selected) => selected.id === modifier.id)
	);

	const textures = useCopyTexture(pillar.selectedMaterials[pillar.id]?.textures, {
		repeat: pillar.selectedMaterials[pillar.id]?.settings.repeat,
	});

	const pillarBase = {
		width: pillarDimensions.pillar_width / 500,
		height: outerHeight,
		length: pillarDimensions.pillar_length / 500,
	};

	const plankSize = {
		smallWidth: pillarBase.width / 2 - 0.015 / 2,
		largeWidth: pillarBase.length / 2 + 0.015 / 2,
		height: outerHeight + 0.06,
		length: 0.015,
	};

	useEffect(() => {
		const mesh = meshRef.current;
		if (!mesh) return;

		const geometry = mesh.geometry;
		if (!geometry) return;

		// Check if the geometry is new
		if (lastGeometryRef.current !== geometry) {
			rotateUVs(geometry, {
				front: 180,
				left: 180,
				right: 180,
				back: 180,
				top: 180,
				bottom: 90,
			});

			// Store the new geometry reference
			lastGeometryRef.current = geometry;
		}
	}, [textures]); // Runs only when geometry might change

	return (
		<group
			position={[
				doorWidth + plankSize.smallWidth / 2 + plankSize.length * 2,
				-0.003,
				-(pillarBase.length / 2) - plankSize.length,
			]}>
			<RoundedBox
				ref={meshRef}
				args={[
					plankSize.smallWidth * 2 + plankSize.length * 2 + minGap * 2,
					plankSize.length,
					pillarBase.length + plankSize.length,
				]}
				radius={0.001} // Bevel radius
				smoothness={0.1} // Higher for smoother bevel
				bevelSegments={20}
				receiveShadow
				castShadow
				position={[
					plankSize.smallWidth / 2 + plankSize.length * 2 - 0.023,
					pillarBase.height + 0.06 + plankSize.length / 2,
					plankSize.largeWidth - plankSize.length / 2,
				]}>
				<meshStandardMaterial
					{...textures}
					color={pillar.selectedMaterials[pillar.id]?.settings.color}
					roughness={pillar.selectedMaterials[pillar.id]?.settings.roughness}
				/>
			</RoundedBox>
			<PlankGroupSmall
				position={[plankSize.length / 2, plankSize.height - plankSize.height / 2, 0]}
				outerHeight={outerHeight}
				plankSize={plankSize}
				minGap={minGap}
				pillarPosition={pillarPosition}
			/>
			<PlankGroupSmall
				position={[plankSize.length / 2, plankSize.height - plankSize.height / 2, pillarBase.length]}
				outerHeight={outerHeight}
				plankSize={plankSize}
				minGap={minGap}
				pillarPosition={pillarPosition}
			/>
			<PlankGroupLarge
				position={[0, plankSize.height - plankSize.height / 2, 0]}
				outerHeight={outerHeight}
				plankSize={plankSize}
				minGap={minGap}
				pillarPosition={pillarPosition}
			/>
			<PlankGroupLarge
				position={[
					plankSize.smallWidth * 2 + (plankSize.length + minGap * 2),
					plankSize.height - plankSize.height / 2,
					0,
				]}
				outerHeight={outerHeight}
				plankSize={plankSize}
				minGap={minGap}
				pillarPosition={pillarPosition}
			/>
			{selectedModifiers &&
				selectedModifiers.map((mod, index) => (
					<ActiveModifier
						key={index}
						mod={mod}
						pillarPosition={pillarPosition}
						position={[
							-(plankSize.smallWidth / 2) + plankSize.length / 2 + plankSize.smallWidth - 0.05,
							pillarBase.height / 2,
							pillarBase.length + plankSize.length / 2 + 0.002,
						]}
					/>
				))}
		</group>
	);
}
